import type { Clock, TimerRecord, TimerState } from '../schemas/entities';
import type { Format, Segment, SideIndex } from '../schemas/format';

/**
 * Timer as a pure state machine over wall-clock timestamps.
 * Only transitions are stored; the displayed time is always computed from `now`,
 * so refreshes, tab switches and crashes never lose or drift time.
 */

export type ClockKey = 'speech' | 'prep0' | 'prep1';

export type TimerCommand =
  | { t: 'toggle' } // start/pause the speech clock
  | { t: 'start' }
  | { t: 'pause' }
  | { t: 'done' } // finish current segment, advance
  | { t: 'goto'; index: number }
  | { t: 'restart' } // restart current segment from full time
  | { t: 'togglePrep'; side: SideIndex }
  | { t: 'adjust'; clock: ClockKey; deltaMs: number } // +deltaMs = more time remaining
  | { t: 'set'; clock: ClockKey; remainingMs: number };

export type Phase = 'ready' | 'running' | 'paused' | 'warn' | 'critical' | 'grace' | 'over';

const clock = (budgetMs: number): Clock => ({ budgetMs, usedMs: 0, runningSince: null });

export const usedMs = (c: Clock, now: number) => c.usedMs + (c.runningSince !== null ? Math.max(0, now - c.runningSince) : 0);
export const remainingMs = (c: Clock, now: number) => c.budgetMs - usedMs(c, now);

const fold = (c: Clock, now: number): Clock => ({ ...c, usedMs: usedMs(c, now), runningSince: c.runningSince !== null ? now : null });
const stop = (c: Clock, now: number): Clock => ({ ...c, usedMs: usedMs(c, now), runningSince: null });
const run = (c: Clock, now: number): Clock => (c.runningSince !== null ? c : { ...c, runningSince: now });

export const fmt = (ms: number): string => {
  const neg = ms < 0;
  const total = Math.abs(ms);
  // Count down shows ceiling (4:00 until a full second passes); overtime shows floor.
  const secs = neg ? Math.floor(total / 1000) : Math.ceil(total / 1000);
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${neg ? '+' : ''}${m}:${String(s).padStart(2, '0')}`;
};

export const initialTimer = (format: Format): TimerState => ({
  cursor: 0,
  speech: clock((format.segments[0]?.durationSec ?? 0) * 1000),
  prep: [clock(format.prepSec * 1000), clock(format.prepSec * 1000)],
  running: null,
  actuals: {},
  log: [],
});

const getClock = (s: TimerState, k: ClockKey): Clock => (k === 'speech' ? s.speech : s.prep[k === 'prep0' ? 0 : 1]);
const setClock = (s: TimerState, k: ClockKey, c: Clock): TimerState =>
  k === 'speech' ? { ...s, speech: c } : { ...s, prep: k === 'prep0' ? [c, s.prep[1]] : [s.prep[0], c] };

const stopAll = (s: TimerState, now: number): TimerState => ({
  ...s,
  speech: stop(s.speech, now),
  prep: [stop(s.prep[0], now), stop(s.prep[1], now)],
  running: null,
});

const startClock = (s: TimerState, k: ClockKey, now: number): TimerState => {
  const stopped = stopAll(s, now);
  return { ...setClock(stopped, k, run(getClock(stopped, k), now)), running: k };
};

const log = (s: TimerState, now: number, what: string): TimerState => ({ ...s, log: [...s.log, { at: now, what }].slice(-500) });

const segLabel = (format: Format, i: number) => {
  const seg = format.segments[i];
  return seg ? `${seg.id} ${seg.label}` : `#${i}`;
};

const clockLabel = (format: Format, k: ClockKey) =>
  k === 'speech' ? 'speech' : `${format.sideLabels[k === 'prep0' ? 0 : 1]} prep`;

export function decide(state: TimerState, cmd: TimerCommand, format: Format, now: number): TimerState {
  const segCount = format.segments.length;
  switch (cmd.t) {
    case 'toggle':
      return state.running === 'speech' ? decide(state, { t: 'pause' }, format, now) : decide(state, { t: 'start' }, format, now);
    case 'start':
      if (state.cursor >= segCount) return state;
      return log(startClock(state, 'speech', now), now, `start ${segLabel(format, state.cursor)}`);
    case 'pause':
      if (state.running === null) return state;
      return log(stopAll(state, now), now, `pause ${clockLabel(format, state.running)}`);
    case 'togglePrep': {
      const k: ClockKey = cmd.side === 0 ? 'prep0' : 'prep1';
      if (state.running === k) return log(stopAll(state, now), now, `stop ${clockLabel(format, k)}`);
      return log(startClock(state, k, now), now, `start ${clockLabel(format, k)}`);
    }
    case 'done': {
      if (state.cursor >= segCount) return state;
      const seg = format.segments[state.cursor]!;
      const stopped = stopAll(state, now);
      const used = stopped.speech.usedMs;
      const next = state.cursor + 1;
      const nextSeg = format.segments[next];
      return log(
        {
          ...stopped,
          actuals: { ...stopped.actuals, [seg.id]: used },
          cursor: next,
          speech: clock((nextSeg?.durationSec ?? 0) * 1000),
        },
        now,
        `done ${segLabel(format, state.cursor)} (${fmt(used)} used)`,
      );
    }
    case 'goto': {
      const index = Math.max(0, Math.min(segCount - 1, Math.trunc(cmd.index)));
      if (index === state.cursor) return state;
      const stopped = stopAll(state, now);
      return log(
        { ...stopped, cursor: index, speech: clock(format.segments[index]!.durationSec * 1000) },
        now,
        `jump to ${segLabel(format, index)}`,
      );
    }
    case 'restart': {
      if (state.cursor >= segCount) return state;
      const stopped = stopAll(state, now);
      return log({ ...stopped, speech: clock(format.segments[state.cursor]!.durationSec * 1000) }, now, `restart ${segLabel(format, state.cursor)}`);
    }
    case 'adjust': {
      const c = fold(getClock(state, cmd.clock), now);
      // More remaining = less used. Never "un-use" time below zero.
      const nextUsed = Math.max(0, c.usedMs - cmd.deltaMs);
      const sign = cmd.deltaMs >= 0 ? '+' : '−';
      return log(setClock(state, cmd.clock, { ...c, usedMs: nextUsed }), now, `${sign}${Math.abs(cmd.deltaMs) / 1000}s ${clockLabel(format, cmd.clock)}`);
    }
    case 'set': {
      const c = fold(getClock(state, cmd.clock), now);
      const remaining = Math.max(0, Math.min(c.budgetMs, cmd.remainingMs));
      return log(setClock(state, cmd.clock, { ...c, usedMs: c.budgetMs - remaining }), now, `set ${clockLabel(format, cmd.clock)} to ${fmt(remaining)}`);
    }
  }
}

/** Apply a command and remember the previous state for undo. Pure. */
export function applyTimer(rec: TimerRecord, cmd: TimerCommand, format: Format, now: number): TimerRecord {
  const next = decide(rec.state, cmd, format, now);
  if (next === rec.state) return rec;
  return { ...rec, state: next, undo: [...rec.undo, rec.state].slice(-30), updatedAt: now };
}

export function undoTimer(rec: TimerRecord, now: number): TimerRecord {
  const prev = rec.undo.at(-1);
  if (!prev) return rec;
  return { ...rec, state: { ...prev, log: [...rec.state.log, { at: now, what: 'undo' }].slice(-500) }, undo: rec.undo.slice(0, -1), updatedAt: now };
}

export interface ClockView {
  remainingMs: number;
  usedMs: number;
  budgetMs: number;
  running: boolean;
  phase: Phase;
  label: string;
}

export const phaseOf = (c: Clock, now: number, graceMs: number): Phase => {
  const r = remainingMs(c, now);
  const running = c.runningSince !== null;
  if (r < -graceMs) return 'over';
  if (r < 0) return 'grace';
  if (running && r <= 10_000) return 'critical';
  if (running && r <= 30_000) return 'warn';
  if (running) return 'running';
  return usedMs(c, now) === 0 ? 'ready' : 'paused';
};

export const viewClock = (c: Clock, now: number, graceMs: number): ClockView => {
  const r = remainingMs(c, now);
  return { remainingMs: r, usedMs: usedMs(c, now), budgetMs: c.budgetMs, running: c.runningSince !== null, phase: phaseOf(c, now, graceMs), label: fmt(r) };
};

/** Thresholds crossed between two instants — used to fire alerts exactly once per crossing. */
export const crossedThresholds = (c: Clock, from: number, to: number, thresholdsMs: number[]): number[] =>
  thresholdsMs.filter((t) => remainingMs(c, from) > t && remainingMs(c, to) <= t);

/**
 * Parli: may the other team offer points of information right now? Only in constructives, and not in the first or
 * last `poiProtectedSec` of the speech ("protected time"). null when the format has no such rule or it's not a constructive.
 */
export const poiWindow = (format: Pick<Format, 'poiProtectedSec'>, seg: Pick<Segment, 'role'> | null, view: Pick<ClockView, 'usedMs' | 'remainingMs'> | null): 'open' | 'protected' | null => {
  if (!format.poiProtectedSec || !seg || seg.role !== 'constructive' || !view) return null;
  const p = format.poiProtectedSec * 1000;
  return view.usedMs < p || view.remainingMs <= p ? 'protected' : 'open';
};
